class CollectionStateManager {
  constructor() {
    this.storageKey = 'collection_state';
    this.currentPageType = this.getCurrentPageType();
    this.currentIdentifier = this.getCurrentIdentifier();
    this.init();
  }

  init() {
    if (!this.currentIdentifier) return;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupEventListeners());
    } else {
      this.setupEventListeners();
    }
  }

  setupEventListeners() {
    this.bindLoadMoreEvents();
    this.bindNavigationEvents();
  }

  getCurrentPageType() {
    const productGrid = document.querySelector('#product-grid');
    if (productGrid && productGrid.dataset && productGrid.dataset.pageType) {
      return productGrid.dataset.pageType;
    }

    if (window.location.pathname.includes('/search')) return 'search';
    if (window.location.pathname.includes('/collections/')) return 'collection';
    return null;
  }

  getCurrentIdentifier() {
    if (this.currentPageType === 'search') {
      return this.getSearchTerms();
    } else if (this.currentPageType === 'collection') {
      return this.getCollectionHandle();
    }
    return null;
  }

  getSearchTerms() {
    const urlParams = new URLSearchParams(window.location.search);
    let searchTerms = urlParams.get('q');

    if (!searchTerms) {
      const productGrid = document.querySelector('#product-grid');
      if (productGrid && productGrid.dataset && productGrid.dataset.searchTerms) {
        searchTerms = productGrid.dataset.searchTerms;
      }
    }
    return searchTerms;
  }

  getCollectionHandle() {
    const urlParams = new URLSearchParams(window.location.search);
    let collectionHandle = urlParams.get('collection');

    if (!collectionHandle) {
      const dataElement = document.querySelector('[data-collection-handle]');
      if (dataElement && dataElement.dataset) {
        collectionHandle = dataElement.dataset.collectionHandle;
      }
    }

    if (!collectionHandle) {
      const pathMatch = window.location.pathname.match(/\/collections\/([^\/]+)/);
      if (pathMatch) {
        collectionHandle = pathMatch[1];
      }
    }
    return collectionHandle;
  }

  saveState() {
    if (!this.currentIdentifier) return;

    const productGrid = document.querySelector('#product-grid');
    if (!productGrid) return;

    const state = {
      pageType: this.currentPageType,
      identifier: this.currentIdentifier,
      currentPage: parseInt(productGrid.dataset.currentPage) || 1,
      totalPages: parseInt(productGrid.dataset.totalPages) || 1,
      totalProducts: parseInt(productGrid.dataset.totalProducts) || 0,
      productsPerPage: parseInt(productGrid.dataset.perPage) || 12,
      nextUrl: productGrid.dataset.nextUrl || '',
      currentCount: this.getCurrentProductCount(),
      scrollPosition: window.scrollY,
      timestamp: Date.now(),
      url: window.location.href
    };

    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify(state));
    } catch (error) {
      console.error('Error saving state:', error);
    }
  }

  getCurrentProductCount() {
    const countElement = document.querySelector('[data-products-current-count]');
    if (countElement) {
      const text = countElement.textContent;
      const match = text.match(/(\d+)/);
      return match ? parseInt(match[1]) : 0;
    }
    return 0;
  }

  restoreState() {
    if (!this.currentIdentifier) return;

    const savedState = this.getSavedState();
    if (!savedState || savedState.pageType !== this.currentPageType || savedState.identifier !== this.currentIdentifier) return;

    const timeDiff = Date.now() - savedState.timestamp;
    if (timeDiff > 5 * 60 * 1000) return; // 5 minutes

    this.restoreLoadMoreState(savedState);
  }

  getSavedState() {
    try {
      const saved = sessionStorage.getItem(this.storageKey);
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.error('Error parsing saved state:', error);
      return null;
    }
  }

  restoreLoadMoreState(state) {
    const productGrid = document.querySelector('#product-grid');
    if (!productGrid) return;

    const currentPageInDOM = parseInt(productGrid.dataset.currentPage) || 1;

    if (state.currentPage > 1 && currentPageInDOM === 1) {
      this.loadPreviouslyLoadedProducts(state.currentPage, productGrid);
    } else {
      this.updateProductGridState(state, productGrid);
    }

    this.scrollToSavedPosition();
  }

  async loadPreviouslyLoadedProducts(targetPage, productGrid) {
    const loadMoreBtn = document.getElementById('load-more');
    const countElement = document.querySelector('[data-products-current-count]');
    const productsPerPage = parseInt(productGrid.dataset.perPage) || 12;
    const totalProducts = parseInt(productGrid.dataset.totalProducts) || 0;
    const totalPages = parseInt(productGrid.dataset.totalPages) || 1;

    for (let page = 2; page <= targetPage; page++) {
      try {
        const pageUrl = this.buildPageUrl(page);
        const response = await fetch(pageUrl);
        const data = await response.text();

        const parser = new DOMParser();
        const htmlDoc = parser.parseFromString(data, "text/html");
        const newProductGrid = htmlDoc.querySelector("#product-grid");

        if (newProductGrid) {
          const newProducts = newProductGrid.innerHTML;
          productGrid.dataset.nextUrl = newProductGrid.dataset.nextUrl;
          productGrid.dataset.currentPage = page.toString();
          productGrid.insertAdjacentHTML("beforeend", newProducts);

          let currentCount = productsPerPage * page;
          if (currentCount > totalProducts) currentCount = totalProducts;

          if (countElement && currentCount > 0) {
            const originalText = countElement.textContent;
            const newText = originalText.replace(/\d+/, currentCount);
            countElement.textContent = newText;
          }
        }
      } catch (error) {
        console.error('Error loading page', page, error);
        break;
      }
    }

    if (loadMoreBtn) {
      loadMoreBtn.style.display = targetPage >= totalPages ? 'none' : 'block';
    }

    this.scrollToSavedPosition();
  }

  buildPageUrl(page) {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('page', page.toString());
    return currentUrl.toString();
  }

  updateProductGridState(state, productGrid) {
    const loadMoreBtn = document.getElementById('load-more');
    const countElement = document.querySelector('[data-products-current-count]');

    productGrid.dataset.currentPage = state.currentPage.toString();
    productGrid.dataset.nextUrl = state.nextUrl;
    productGrid.dataset.totalPages = state.totalPages.toString();
    productGrid.dataset.totalProducts = state.totalProducts.toString();
    productGrid.dataset.perPage = state.productsPerPage.toString();

    if (countElement && state.currentCount > 0) {
      const originalText = countElement.textContent;
      const newText = originalText.replace(/\d+/, state.currentCount);
      countElement.textContent = newText;
    }

    if (loadMoreBtn) {
      loadMoreBtn.style.display = state.currentPage >= state.totalPages ? 'none' : 'block';
    }
  }

  scrollToSavedPosition() {
    const savedState = this.getSavedState();
    if (savedState && savedState.scrollPosition) {
      setTimeout(() => {
        window.scrollTo({
          top: savedState.scrollPosition,
          behavior: 'instant'
        });
      }, 300);
    }
  }

  bindLoadMoreEvents() {
    const loadMoreBtn = document.getElementById('load-more');
    if (!loadMoreBtn) return;

    loadMoreBtn.addEventListener('click', () => {
      this.saveState();
      this.handleLoadMore(loadMoreBtn);
    });
  }

  handleLoadMore(loadMoreBtn) {
    const productGrid = document.querySelector('#product-grid');
    const productCurrentCount = document.querySelector('[data-products-current-count]');
    if (!productGrid) return;

    const totalPages = parseInt(productGrid.dataset.totalPages);
    const totalProducts = parseInt(productGrid.dataset.totalProducts);
    const productsPerPage = parseInt(productGrid.dataset.perPage);

    loadMoreBtn.classList.add('loading');
    const nextUrl = productGrid.dataset.nextUrl;
    const currentPage = parseInt(productGrid.dataset.currentPage) + 1;

    fetch(nextUrl)
      .then((response) => response.text())
      .then((data) => {
        const parser = new DOMParser();
        const htmlDoc = parser.parseFromString(data, "text/html");
        const newProductGrid = htmlDoc.querySelector("#product-grid");

        if (newProductGrid) {
          const newProducts = newProductGrid.innerHTML;
          productGrid.dataset.nextUrl = newProductGrid.dataset.nextUrl;
          productGrid.dataset.currentPage = currentPage.toString();
          productGrid.insertAdjacentHTML("beforeend", newProducts);

          let currentCount = productsPerPage * currentPage;
          if (currentCount > totalProducts) currentCount = totalProducts;

          if (currentPage >= totalPages) {
            loadMoreBtn.style.display = "none";
          }
          if (currentCount > 0) {
            productCurrentCount.innerHTML = currentCount;
          }
        }
        loadMoreBtn.classList.remove('loading');
        this.saveState();
        document.body.dispatchEvent(new Event('showTrendingTag'));
        document.body.dispatchEvent(new Event('showBadges'));
      })
      .catch((error) => {
        console.error('Error loading more products:', error);
        loadMoreBtn.classList.remove('loading');
      });
  }

  bindNavigationEvents() {
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a');
      if (link && link.href && link.href.includes('/products/')) {
        this.saveState();
        sessionStorage.setItem('navigating_to_product', 'true');
      }
    });

    window.addEventListener('beforeunload', () => this.saveState());

    window.addEventListener('popstate', () => {
      setTimeout(() => this.restoreState(), 100);
    });

    window.addEventListener('pageshow', (event) => {
      const wasNavigatingToProduct = sessionStorage.getItem('navigating_to_product');
      if (wasNavigatingToProduct === 'true') {
        sessionStorage.removeItem('navigating_to_product');
        setTimeout(() => this.restoreState(), 100);
      }
    });
  }

  clearState() {
    sessionStorage.removeItem(this.storageKey);
  }
}

// Initialize the collection state manager
document.addEventListener('DOMContentLoaded', () => {
  if (document.querySelector('#product-grid') || window.location.pathname.includes('/collections/')) {
    window.collectionStateManager = new CollectionStateManager();
  }
});

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CollectionStateManager;
}
